/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
/* eslint-disable ngrx/avoid-dispatching-multiple-actions-sequentially */
import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import {
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { OneTimePayActions, OneTimePayFeatureFacade } from '@amfam/billing/one-time-pay/feature';
import { paymentHistoryQuery } from '@amfam/billing/payment-history/data-access';
import {
  PaymentMethodActions,
  PaymentMethodSelectors
} from '@amfam/billing/payment-method/data-access';
import { PaymentMethodFeatureActions } from '@amfam/billing/payment-method/feature';
import {
  PaymentMethod,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType,
  PaymentMethodUtilService
} from '@amfam/billing/payment-method/util';
import {
  pendingPaymentActions,
  pendingPaymentQuery
} from '@amfam/billing/pending-payment/data-access';
import {
  BillingRegistrationSelectors,
  PendingRegistrationsActions
} from '@amfam/billing/registration/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
// Store
import { selectBillaccountsListNotEligibleForAutopay } from '@amfam/billing/shared/feature';
import { BillAccountTransmuteService } from '@amfam/billing/shared/util';
import { policiesQuery } from '@amfam/policy/data-access';
import { AnalyticsActions, AnalyticsFacade } from '@amfam/shared/analytics';
import {
  AutomaticPayment,
  BillAccount,
  BillingPaymentPaths,
  EntityNotification,
  INVALID_IMPERSONATION_STATUS_CODE,
  OverviewAnalytics,
  Payment,
  PolicyChangeRequestType,
  ScheduledPayment,
  TryAgainRegistrationModel
} from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService
} from '@amfam/shared/utility/shared-services';
import { AlertModalConfig, DsModalService, LoadingSpinnerService } from '@amfam/ui-kit';

import {
  BillAccountActionButtons,
  BillAccountCard,
  BillAccountCardMetadata,
  BillAccountCardStatus
} from '../../models/billing-overview.models';
import { OverviewBillingUtilService } from '../../util/overview-billing-util.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'overview-billing-wrapper',
  templateUrl: './overview-billing-wrapper.component.html',
  styleUrls: ['./overview-billing-wrapper.component.scss'],
  standalone: false
})
export class OverviewBillingWrapperComponent implements OnInit, OnDestroy, AfterContentChecked {
  @Input() navFromBillDash = false;
  @Input() navFromOverview = false;

  billAccountCards$: Observable<BillAccountCard[]> = of([]);
  dataLoading$: Observable<boolean> = of(false);
  billAccountsError$: Observable<boolean> = of(false);
  impersonationError = false;
  isMyAccountAdmin = false;
  romeFeatureBanner: boolean;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );
  hasMultiBillAccForRome: boolean;
  romeBannerPaymentModalId = 'romeBannerPaymentModalId';

  silentRegistrationAllFailedError: AlertModalConfig =
    this.oneTimePayFacade.silentRegistrationAllFailedError();

  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  private tryAgainRegistration: TryAgainRegistrationModel;

  romeBannerList = this.copyService.getCopy('billing.romeBanner.bannerList');

  sectionTitle: string = this.copyService.getCopy('billing.overview.sectionHeadingMultiple');

  constructor(
    private store: Store,
    private transmuteService: BillAccountTransmuteService,
    private spinner: LoadingSpinnerService,
    private feature: FeatureFlagService,
    private overviewBillingUtil: OverviewBillingUtilService,
    private appService: ApplicationService,
    private autoPayUtil: AutoPayUtilService,
    private paymentMethodUtil: PaymentMethodUtilService,
    private modalService: DsModalService,
    private utilService: UtilService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private copyService: CopyService,
    private analyticsFacade: AnalyticsFacade,
    private router: Router,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Reset state.
    this.store.dispatch(PendingRegistrationsActions.PendingRegistrationsRemoveAll());
    this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
    this.billAccountsError$ = this.store.select(BillAccountsSelectors.selectBillAccountsHasError);
    this.isMyAccountAdmin = this.appService.isApp(Applications.MYACCOUNT_ADMIN);
    this.romeFeatureBanner = this.feature.isEnabled('rome_banner');
    this.setSectionHeader();
    // loading spinner
    this.dataLoading$ = combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(AutoPaySelectors.getAnyLoading),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingPolicyList)
    ]).pipe(
      map(
        ([
          billAccountsLoading,
          automaticPaymentsLoading,
          scheduledPaymentsLoading,
          policyRiskLoading
        ]) =>
          billAccountsLoading ||
          automaticPaymentsLoading ||
          scheduledPaymentsLoading ||
          policyRiskLoading
      )
    );

    // registration spinner
    combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccountsUpdatingRegistration),
      this.store.select(BillingRegistrationSelectors.selectPendingRegistrationError),
      this.store.select(policiesQuery.selectAllPolicies)
    ])
      .pipe(takeUntil(this.stop$))
      .subscribe(([isUpdating, registrationError]) => {
        if (!isUpdating && registrationError) {
          this.spinner.stop();
          this.modalService.open('silentRegistrationErrorModalAllFailed');
        } else if (isUpdating) {
          this.spinner.start();
        }
      });

    this.getBillAccountCards();
  }

  openRomeModal() {
    this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickRomeLearnMore);
    this.modalService.open(this.romeBannerPaymentModalId);
    this.analyticsFacade.trackPage(OverviewAnalytics.romeModalOpen);
  }

  getBillAccountCards() {
    this.billAccountCards$ = combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(BillAccountsSelectors.selectAllBillAccountsNotifications),
      this.store.select(selectBillaccountsListNotEligibleForAutopay),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPayments),
      this.store.select(ScheduledPaymentSelectors.selectSuccessfulPaymentsAllMethods),
      this.store.select(ScheduledPaymentSelectors.selectFailedPaymentsAllMethods),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(AutoPaySelectors.getAutoPayNotifications),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods),
      this.store.select(paymentHistoryQuery.selectPayments)
    ]).pipe(
      map(
        ([
          billAccounts,
          billAccountNotifications,
          billAccountsNotEligibleForAutoPay,
          scheduledPayments,
          successfulPayments,
          failedPayments,
          automaticPayments,
          automaticPaymentNotifications,
          paymentMethods,
          paymentHistory
        ]: [
          BillAccount[],
          BillingNotificationsState[],
          BillAccount[],
          ScheduledPayment[],
          ScheduledPayment[],
          ScheduledPayment[],
          AutomaticPayment[],
          EntityNotification[],
          PaymentMethod[],
          Payment[]
        ]) => {
          const billAccountCards: BillAccountCard[] = [];

          const automaticPaymentsToggle: AutomaticPayment[] = automaticPayments;
          const automaticPaymentNotificationsToggle: EntityNotification[] =
            automaticPaymentNotifications;

          const filteredPaymentMethods = paymentMethods?.filter(function (x) {
            return x !== undefined;
          });

          const paymentMethodsToggle: PaymentMethod[] = filteredPaymentMethods;

          billAccounts.forEach((billAccount: BillAccount) => {
            // for a non migrated rome policy or any other normal policy isRomeNew and isRomeOld both will be false
            let isRomeNew = false;
            let isRomeOld = false;

            let pairFound = false;

            // Finding a bill account pair based on if the policy number is repeated in any other bill acc
            if (billAccounts.every(billAc => billAc.policyList)) {
              for (const policy of billAccount.policyList) {
                if (pairFound) {
                  break; // No need to look for other policies once a pair is found in previous iteration
                }

                for (const billAcc of billAccounts) {
                  // Preventing self match
                  if (billAcc.billAccountNumber !== billAccount.billAccountNumber) {
                    // if policy.policyNumber is present in the policyList of billAcc then pairFound is true
                    if (billAcc.policyList.some(p => p.policyNumber === policy.policyNumber)) {
                      pairFound = true;
                      break; // No need to look for other bill accounts once a pair bill account is found
                    }
                  }
                }
              }
            }

            if (pairFound) {
              this.hasMultiBillAccForRome = true;
              isRomeNew = billAccount.isAdvance;
              isRomeOld = !billAccount.isAdvance;
            }

            const cardMetadata: BillAccountCardMetadata =
              this.overviewBillingUtil.getBillAccountCardMetadata(
                billAccount,
                billAccountNotifications,
                billAccountsNotEligibleForAutoPay,
                scheduledPayments,
                successfulPayments,
                failedPayments,
                automaticPaymentsToggle,
                automaticPaymentNotificationsToggle,
                paymentMethodsToggle,
                paymentHistory,
                isRomeNew,
                isRomeOld
              );
            const cardStatus: BillAccountCardStatus =
              this.overviewBillingUtil.getBillAccountCardStatus(cardMetadata);

            billAccountCards.push({
              icon: billAccount.icon,
              policyType: billAccount.policyTypeDisplayName,
              alert: billAccount.pastDue,
              status: cardStatus,
              metadata: cardMetadata
            });
          });

          return billAccountCards.sort(this.compareBills);
        }
      )
    );
  }

  // sorts by due date all with no minimum payment to end; the rest soonest -> latest
  private compareBills(a: BillAccountCard, b: BillAccountCard): number {
    // these will be true if no amount is due
    const nothingDueA: boolean = a.metadata.minimumDue * 1 === 0;
    const nothingDueB: boolean = b.metadata.minimumDue * 1 === 0;

    // if both have minimum of zero, sort the higher account balance to front
    if (nothingDueA && nothingDueB) {
      return Number(a.metadata.accountBalance) * 1 > Number(b.metadata.accountBalance) * 1
        ? 1
        : Number(a.metadata.accountBalance) * 1 < Number(b.metadata.accountBalance) * 1
          ? -1
          : 0;
    }

    // zero min due always goes to back
    if (nothingDueB) {
      return -1;
    }
    if (nothingDueA) {
      return 1;
    }

    return a.metadata.minimumDueDate > b.metadata.minimumDueDate
      ? 1
      : a.metadata.minimumDueDate < b.metadata.minimumDueDate
        ? -1
        : 0;
  }

  viewAccount(billAccountCard: BillAccountCard) {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.BILLING_SECTION_PAGE, billAccountCard.metadata.billAccountNumber]
      })
    );
  }

  deriveRoutingPath(billAccountCard: BillAccountCard): void {
    if (
      billAccountCard.metadata.isPastDue &&
      !billAccountCard.metadata.isAdvance &&
      this.feature.isEnabled('classic_past_due')
    ) {
      if (this.appService.isApp(Applications.MYACCOUNT_ADMIN)) {
        this.store.dispatch(
          AnalyticsActions.sendAnalytic({
            options: { button: OverviewAnalytics.overviewAdminPastDueCard }
          })
        );
      } else {
        this.analyticsFacade.trackButtonClick(OverviewAnalytics.overviewPastDueCard);
      }
    }
    let routeToMakeAPayment: string;
    // ONE TIME PAYMENT ENTRYPOINT
    if (this.feature.isEnabled('simplified_payment')) {
      routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
    } else {
      this.store
        .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
        .pipe(take(1))
        .subscribe(billAccounts => {
          if (billAccounts.length === 1) {
            routeToMakeAPayment = BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT;
          } else {
            routeToMakeAPayment = BillingPaymentPaths.ADD_ONE_TIME_PAYMENT;
          }
        });
    }
    if (billAccountCard.metadata.hasAFT || billAccountCard.metadata.isRegisteredElseWhere) {
      this.store.dispatch(
        fromRouterActions.Go({
          path: [
            BillingPaymentPaths.BILLING_SECTION_PAGE,
            billAccountCard.metadata.billAccountNumber
          ]
        })
      );
    } else if (
      !billAccountCard.metadata.isOnlineBilling &&
      billAccountCard.status.actionButton === BillAccountActionButtons.MAKE_PAYMENT
    ) {
      this.initiateSilentRegistration(
        billAccountCard.metadata.billAccountNumber,
        routeToMakeAPayment
      );
    } else if (
      !billAccountCard.metadata.isOnlineBilling &&
      billAccountCard.status.actionButton === BillAccountActionButtons.ADD_AUTOPAY
    ) {
      // AUTOPAY ENTRY POINT
      this.autoPayUtil.routeToMultipleAutoPaySelection('overview', [
        billAccountCard.metadata.billAccountNumber
      ]);
    } else {
      switch (billAccountCard.status.actionButton) {
        case BillAccountActionButtons.VIEW_PAYMENTS:
          this.store.dispatch(
            fromRouterActions.Go({
              path: [
                BillingPaymentPaths.BILLING_DETAIL_PAGE,
                billAccountCard.metadata.billAccountNumber
              ]
            })
          );
          break;
        case BillAccountActionButtons.EDIT_PAYMENT:
          this.store.dispatch(
            OneTimePayActions.routeToReviewForEdit({
              paymentId: billAccountCard.metadata.scheduledPayments[0].paymentId
            })
          );

          break;
        case BillAccountActionButtons.EDIT_AUTOPAY:
          // AUTOPAY ENTRY POINT
          this.autoPayUtil.editAutoPay(billAccountCard.metadata.billAccountNumber, 'overview');

          break;
        case BillAccountActionButtons.MAKE_PAYMENT:
          this.store.dispatch(
            pendingPaymentActions.AddPendingPaymentAction({
              billAccountNumber: billAccountCard.metadata.billAccountNumber
            })
          );
          // ONE TIME PAYMENT ENTRYPOINT
          if (this.feature.isEnabled('simplified_payment')) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
              })
            );
          } else {
            this.store
              .select(pendingPaymentQuery.selectBillAccountsEligibleForOneTimePay)
              .pipe(take(1))
              .subscribe(billAccounts => {
                if (billAccounts.length === 1) {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
                    })
                  );
                } else {
                  this.store.dispatch(
                    fromRouterActions.Go({
                      path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
                    })
                  );
                }
              });
          }
          break;
        case BillAccountActionButtons.ADD_AUTOPAY:
          // AUTOPAY ENTRY POINT
          this.store
            .select(AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay)
            .pipe(take(1))
            .subscribe(eligibleAccounts => {
              if (eligibleAccounts.length > 1) {
                this.autoPayUtil.routeToMultipleAutoPaySelection('overview', [
                  billAccountCard.metadata.billAccountNumber
                ]);
              } else {
                this.autoPayUtil.addAutoPay(billAccountCard.metadata.billAccountNumber, 'overview');
              }
            });

          this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickAutoPay);
          break;
        case BillAccountActionButtons.EDIT_PAYMENT_ACCOUNT:
          if (billAccountCard.metadata.expiredOrExpiringPaymentAccounts.length === 1) {
            const paymentAccountId: string =
              billAccountCard.metadata.expiredOrExpiringPaymentAccounts[0];
            this.store
              .select(PaymentMethodSelectors.selectPaymentMethod(paymentAccountId))
              .pipe(take(1))
              .subscribe((paymentMethod: PaymentMethod) => {
                const config: PaymentMethodAddEditConfig = {
                  accountType: this.paymentMethodUtil.getPaymentMethodAccountType(paymentMethod),
                  operation: PaymentMethodAddEditOperationType.EDIT,
                  paymentMethod
                };
                this.paymentMethodAddEditConfig$.next(config);
                this.modalService.open('paymentMethodModal');
              });
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.BILLING_SECTION_PAGE],
                query: { openAccordion: 'paymentMethods' }
              })
            );
          }
          break;
        case BillAccountActionButtons.EDIT_LENDER:
          this.router.navigateByUrl(`/policies/changerequest`, {
            state: {
              changeRequestType: PolicyChangeRequestType.MORTGAGE_LENDER_DETAILS_CHANGE
            }
          });
          break;
      }
    }
  }

  private initiateSilentRegistration(billAccountNumber: string, route: string): void {
    this.spinner.start({ blockActions: true });
    this.store.dispatch(PendingRegistrationsActions.PendingRegistrationsRemoveAll());
    this.transmuteService
      .initiateSilentRegistration([billAccountNumber])
      .pipe(
        /**
         * AS: Filter the payload till we have a valid billaccount, we are only concerned
         * with the first payload as we only send one bill account, the function however takes and returns
         * an array of bill accounts.
         * We Stop the subscription once we have a valid response.
         * */
        take(1),
        map(billAccountArr =>
          billAccountArr.find(ba => ba.billAccountNumber === billAccountNumber)
        ),
        withLatestFrom(
          this.store.select(
            BillAccountsSelectors.selectMatchedBillAccountNotification,
            billAccountNumber
          )
        )
      )
      .subscribe(([billingAccount, matchedBillingNotificationState]) => {
        // Route to make a payment or add autopay section if bill account is successfully registered.
        if (_get(billingAccount, 'registrationDate')) {
          if (
            route === BillingPaymentPaths.ADD_ONE_TIME_PAYMENT ||
            route === BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT
          ) {
            this.store.dispatch(
              pendingPaymentActions.AddPendingPaymentAction({
                billAccountNumber: billingAccount.billAccountNumber
              })
            );
            this.store.dispatch(
              fromRouterActions.Go({
                path: [route]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [route]
              })
            );
          }
        } else {
          // Fail case
          if (
            route === BillingPaymentPaths.ADD_ONE_TIME_PAYMENT ||
            route === BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT
          ) {
            this.store.dispatch(pendingPaymentActions.ClearPendingPaymentsAction());
          }
          // Display error modal
          this.modalService.open('silentRegistrationErrorModalAllFailed');

          if (
            matchedBillingNotificationState.updateRegistrationError ===
            INVALID_IMPERSONATION_STATUS_CODE
          ) {
            // Failed due to impersonation
            this.impersonationError = true;
          } else {
            // Failed for any other reason
            // Let Try Again CTA know which bill account to register.
            this.tryAgainRegistration = {
              billAccountNumber: billAccountNumber,
              route: route
            };
          }
        }
        this.spinner.stop();
      });
  }

  /**
   * @description: Try again button was clicked from the sub-component/modal screen to re-submit
   * the silent registration.
   */
  tryAgainBtnClicked() {
    this.initiateSilentRegistration(
      this.tryAgainRegistration.billAccountNumber,
      this.tryAgainRegistration.route
    );
  }

  // complete 'save' event from payment method modal
  paymentMethodAddEditComplete(): void {
    this.modalService.close('paymentMethodModal');

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.store.dispatch(PaymentMethodActions.getPaymentMethods({ correlationId }));
    this.store
      .select(PaymentMethodSelectors.selectPaymentMethodState)
      .pipe(
        filter(state => state.correlationId === correlationId),
        take(1)
      )
      .subscribe(state => {
        this.spinner.stop();

        if (!state.hasError) {
          const operation: 'add' | 'edit' | 'delete-success' | 'delete-error' =
            this.paymentMethodAddEditConfig$.getValue().operation;
          this.store.dispatch(PaymentMethodFeatureActions.setPaymentMethodOperation({ operation }));
          //route
          this.store.dispatch(
            fromRouterActions.Go({
              path: [BillingPaymentPaths.CONFIRMATION_PAYMENT_METHOD]
            })
          );
        }
      });
  }

  closeSilentRegistrationErrorModal() {
    this.modalService.closeAll();
  }

  setSectionHeader() {
    if (this.isMyAccountAdmin) {
      this.sectionTitle = this.copyService.getCopy('billing.overview.sectionHeadingAdmin');
    } else if (this.navFromBillDash) {
      this.sectionTitle = '';
    } else if (this.navFromOverview) {
      this.sectionTitle = 'Upcoming bills';
    } else {
      this.billAccountCards$.pipe(takeUntil(this.stop$)).subscribe(result => {
        if (result.length === 1) {
          this.sectionTitle = this.copyService.getCopy('billing.overview.sectionHeading');
        }
      });
    }
  }

  ngAfterContentChecked() {
    this.setSectionHeader();
    this.changeDetector.detectChanges();
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
